// The read model: what the screen is allowed to look at.
//
// It answers questions the write model cannot — ordered by date, narrowed to one errand, matching
// some typed text, counted — and it answers them from a shape built for asking, not for storing.
// Nothing here writes; every value below is DERIVED from the entries, and the derivation is the
// only way it can be produced.
//
// IT IS NOT PERSISTED, and that is a decision rather than an omission. On the server a read model
// is written down because reprojecting it is expensive — an index to rebuild, tables to fill. Here
// the whole projection is a map and a sort over entries that already sit in memory: measured in
// microseconds at any size a browser store can reach, against a second copy of everything inside a
// 5 MB budget the write model already has to fit in. So it is rebuilt on demand and thrown away,
// which is the same contract with none of the cost — derived, disposable, never authoritative. If
// volume ever makes that false, persisting it changes nothing above this line.
//
// SEARCHING READS THE LABELS, not the wire names. Someone looking for what they answered types
// "Dupont", never "family-name" — and the labels are already in their own language, because the
// referential rendered them that way when the question was asked.

/* Folded once per entry rather than at every keystroke, and accents removed with it: someone
   searching "eleve" should find "élevé", and nobody types the accents into a search box. */
function folded(text) {
	return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/* The line under a title: the first answers, in the order they were asked. Three, because the
   point is to tell two entries of the same errand apart at a glance — a preview that shows
   everything is the entry itself, and one that shows one field rarely distinguishes anything. */
function previewOf(entry) {
	return entry.answers.slice(0, 3).map((answer) => answer.value).join(' · ');
}

/* One row, ready to draw. The searchable text is built ONCE here rather than recomputed on every
   keystroke — which is the entire reason this projection exists as a step of its own. */
function row(entry) {
	return {
		id: entry.id,
		title: entry.title,
		errand: entry.errand,
		kind: entry.kind,
		source: entry.source,
		origin: entry.origin,
		// ISO-8601, so it sorts lexicographically and never needs parsing to be ordered.
		keptAt: entry.keptAt,
		answers: entry.answers,
		preview: previewOf(entry),
		search: folded([entry.title, entry.errand]
			.concat(entry.answers.map((answer) => answer.label + ' ' + answer.value)).join(' ')),
	};
}

/**
 * The facets: which errands are actually present, and how many entries each holds.
 *
 * Built from the entries rather than from the referential's catalogue of thirty-odd errands, which
 * is the difference between a filter that offers what is there and one that offers mostly empty
 * choices. Labelled with the entry's own title, since that is the errand's name in the reader's
 * language and we have no other.
 */
function facets(rows) {
	const counted = new Map();
	rows.forEach((entry) => {
		const key = entry.errand || '';
		const known = counted.get(key);
		if (known) {
			known.count += 1;
		} else {
			counted.set(key, { errand: key, label: entry.title || key, count: 1 });
		}
	});
	return Array.from(counted.values()).sort((left, right) => left.label.localeCompare(right.label));
}

/**
 * The whole projection, from the entries and what the screen is currently asking for.
 *
 * `total` is deliberately the count BEFORE the query and `rows` the count after: a screen that only
 * knew the filtered figure could not tell "you have kept nothing" from "nothing matches what you
 * typed", and those two need different words — one is an empty store, the other is a bad search.
 *
 * PROFILES ARE NOT ROWS. They are listed by family, on screens of their own (screens.js), and a
 * camera drawn as a card between two contact forms would be filed under nothing. They still count
 * in `total`: a store holding only profiles is not an empty store, and the transfer block must show.
 */
export function project(entries, query) {
	const asked = query || {};
	const all = entries.map(row);

	const wanted = asked.errand
		? all.filter((entry) => entry.errand === asked.errand)
		: all;

	const needle = folded((asked.search || '').trim());
	const matching = needle ? wanted.filter((entry) => entry.search.includes(needle)) : wanted;

	// Copied before sorting: `sort` is in place, and sorting the array a caller handed us is the
	// kind of side effect that is invisible until something else reads it afterwards.
	const ordered = matching.slice().sort((left, right) => (asked.sort === 'oldest'
		? left.keptAt.localeCompare(right.keptAt)
		: right.keptAt.localeCompare(left.keptAt)));

	return {
		rows: ordered,
		total: entries.length,
		matched: ordered.length,
		facets: facets(all),
		// What the screen has to say when there is nothing to draw, decided here where both numbers
		// are known rather than re-derived from two counts at the far end.
		emptiness: entries.length === 0 ? 'store' : (all.length > 0 && ordered.length === 0 ? 'query' : null),
	};
}

export { folded, previewOf };
